import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, Wallet, Package, RefreshCw, BarChart3 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Финдашборд для бухгалтера (и админа):
 *  - Выручка / COGS / Gross profit / Margin % за период
 *  - Закупки за период
 *  - Стоимость складских остатков (по себестоимости)
 *  - Периоды: 7д / 30д / 90д / текущий месяц / произвольный
 */
export default function AdminAccounting() {
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [pnl, setPnl] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);

  const resolveRange = () => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "custom") return { from: customFrom || iso(new Date(today.getTime() - 30 * 86400000)), to: customTo || iso(today) };
    const days = preset === "7d" ? 7 : preset === "90d" ? 90 : preset === "month" ? today.getDate() - 1 : 30;
    const from = new Date(today.getTime() - days * 86400000);
    return { from: iso(from), to: iso(today) };
  };

  const load = async () => {
    setLoading(true);
    try {
      const { from, to } = resolveRange();
      const [p, iv] = await Promise.all([
        axios.get(`${API}/admin/reports/pnl?date_from=${from}&date_to=${to}`, { withCredentials: true }),
        axios.get(`${API}/admin/reports/inventory-value`, { withCredentials: true }),
      ]);
      setPnl(p.data);
      setInventory(iv.data);
    } catch (e) {
      // 403 — non-finance role
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preset, customFrom, customTo]);

  const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#111", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <BarChart3 size={26} color="#C5A059" /> Финансы
          </h2>
          <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginTop: 4 }}>
            Ключевые показатели бизнеса: выручка, себестоимость, прибыль, стоимость склада
          </div>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Обновить</button>
      </div>

      {/* Period selector */}
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginBottom: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b" }}>Период:</span>
        {[
          { id: "7d", label: "7 дней" },
          { id: "30d", label: "30 дней" },
          { id: "90d", label: "90 дней" },
          { id: "month", label: "Этот месяц" },
          { id: "custom", label: "Свой" },
        ].map(p => (
          <button
            key={p.id}
            data-testid={`period-${p.id}`}
            onClick={() => setPreset(p.id)}
            style={{
              background: preset === p.id ? "#C5A059" : "white",
              color: preset === p.id ? "#0A0A0A" : "#374151",
              border: preset === p.id ? "1px solid #C5A059" : "1px solid #d1d5db",
              borderRadius: 6, padding: "6px 14px", cursor: "pointer",
              fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
            }}
          >
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ ...inp, width: 150, marginTop: 0 }} />
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ ...inp, width: 150, marginTop: 0 }} />
          </>
        )}
      </div>

      {loading || !pnl ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontFamily: "Manrope" }}>Загрузка…</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 12, marginBottom: 20 }}>
            <KPI icon={TrendingUp} label="Выручка" value={fmt(pnl.revenue_kzt)} unit="₸" sub={`${pnl.deals_count} сделок`} color="#16a34a" />
            <KPI icon={Package} label="Себестоимость (COGS)" value={fmt(pnl.cogs_kzt)} unit="₸" sub={`${pnl.units_sold} ед. продано`} color="#f59e0b" />
            <KPI icon={Wallet} label="Валовая прибыль" value={fmt(pnl.gross_profit_kzt)} unit="₸" sub={`маржа ${pnl.margin_pct}%`} color="#C5A059" />
            <KPI icon={Package} label="Закупки за период" value={fmt(pnl.purchases_kzt)} unit="₸" sub={`${pnl.arrivals_count} поступл.`} color="#0ea5e9" />
          </div>

          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Состояние склада сейчас</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 20 }}>
              <div>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: 32, fontWeight: 600, color: "#111" }}>{inventory?.total_qty || 0} ед.</div>
                <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b" }}>Товарных единиц на складах</div>
              </div>
              <div>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: 32, fontWeight: 600, color: "#16a34a" }}>{fmt(inventory?.total_value_kzt || 0)} ₸</div>
                <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b" }}>Стоимость по себестоимости</div>
              </div>
            </div>
          </div>

          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 14px", fontFamily: "Manrope", fontSize: 12, color: "#78350f", lineHeight: 1.5 }}>
            <strong>ℹ️ Как считается:</strong><br />
            • Выручка — сумма закрытых успешно (won) сделок в CRM за период<br />
            • Себестоимость — сумма списаний товара со склада (по средней цене приёмки)<br />
            • Валовая прибыль = Выручка − Себестоимость<br />
            • Закупки — сумма проведённых поступлений за период (в ₸ по курсу инвойса)
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, unit, sub, color }) {
  return (
    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
        <Icon size={18} color={color} />
      </div>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 26, color: "#111", fontWeight: 600, marginTop: 6 }}>{value} {unit && <span style={{ fontSize: 16, color: "#64748b" }}>{unit}</span>}</div>
      {sub && <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#64748b", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const inp = { border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 10px", fontSize: 12, marginTop: 6, outline: "none" };
const btnSec = { background: "white", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 600, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 };
